import { baseApi } from 'services/api';
import { API_TAG_TYPES, API_VERSION } from 'constants/common';
import {
  ApproveMemberPayRunPayload,
  ApproveMemberPayRunResponse,
  CreateAdjustmentPayload,
  CustomizeColumnsPayload,
  DeleteMerchantAdjustmentResponse,
  GetCurrentPayPeriodResponse,
  GetJobAdjustmentsRequest,
  GetJobAdjustmentsResponse,
  GetPayRunsPayload,
  GetRecentPayRunsResponse,
  GetSuggestedAdjustmentsForJobResponse,
  MerchantPayrunEmailRequest,
  PayRunMemberDetails,
  PayRunSettings,
  PayRunSettingsPayload,
  PayRunSettingsResponse,
  PayRunsResponse,
  SuggestedAdjustmentsResponse,
  UpdateAdjustmentPayload,
  UpdateAdjustmentStatusPayload,
  UpdateCreateResponse,
  UpdateJobAdjustmentPayload,
  UploadAdjustmentReceiptResponse
} from './payrun.model';
import { getUrlSearchParams } from 'utils/apiUtils';
import { downloadFile } from 'utils/commonUtils';
import { FileTypes } from 'types/baseTypes';

const PayRunsBaseApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    setPayRunSettings: builder.mutation<PayRunSettingsResponse, PayRunSettingsPayload>({
      query: ({ teamId, payload }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/pay-run/settings`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: [API_TAG_TYPES.PAY_RUN_SETTINGS, API_TAG_TYPES.PAY_RUN_CURRENT],
      extraOptions: {
        failure: 'Failed to save pay run settings',
        success: 'Pay run settings saved successfully',
        showToast: true
      }
    }),

    getCurrentPayPeriod: builder.query<GetCurrentPayPeriodResponse, number>({
      query: teamId => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/pay-run/current`,
        method: 'GET'
      }),
      providesTags: [API_TAG_TYPES.PAY_RUN_CURRENT],
      extraOptions: {
        failure: 'Failed to fetch current pay period',
        showToast: true
      }
    }),

    getPayRunSettings: builder.query<PayRunSettings, number>({
      query: teamId => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/pay-run/settings`,
        method: 'GET'
      }),
      providesTags: ['PayRunSettings'],
      extraOptions: {
        failure: 'Failed to fetch pay run settings',
        showToast: false
      }
    }),

    getRecentPayRuns: builder.query<GetRecentPayRunsResponse, number>({
      query: teamId => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/pay-run/recent`,
        method: 'GET'
      }),
      extraOptions: {
        failure: 'Failed to fetch recent pay runs',
        showToast: true
      }
    }),

    getPayRuns: builder.query<PayRunsResponse, GetPayRunsPayload>({
      query: ({ teamId, status, ...params }) => {
        const searchParams = getUrlSearchParams(params);
        const statusParam = status?.length ? `&status=${status.join(',')}` : '';

        return {
          url: `${API_VERSION.V1}/merchants/teams/${teamId}/pay-run?${searchParams}${statusParam}`,
          method: 'GET'
        };
      },
      extraOptions: {
        failure: 'Failed to fetch pay runs',
        showToast: true
      }
    }),

    getPayRunsExport: builder.query<
      void,
      {
        teamId: number;
        status?: string[];
        payPeriodFrom?: string | null;
        payPeriodTo?: string | null;
      }
    >({
      query: ({ status, ...params }) => {
        const queryParams = getUrlSearchParams({
          ...params
        });
        const statusParam = status?.length ? `&status=${status.join(',')}` : '';

        return {
          url: `${API_VERSION.V1}/merchants/teams/${params.teamId}/pay-run/export?${queryParams}${statusParam}`,
          method: 'GET',
          responseHandler: async response => {
            await downloadFile({ response, fileType: FileTypes.CSV, fileName: 'pay-runs' });
          }
        };
      },
      extraOptions: {
        failure: 'Failed to export pay runs data',
        success: 'Pay runs exported successfully!',
        showToast: true
      }
    }),

    getPayRunMemberDetails: builder.query<
      PayRunMemberDetails,
      { teamId: number; payRunId: string; merchantId: string }
    >({
      query: ({ teamId, payRunId, merchantId }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/pay-run/${payRunId}/merchants/${merchantId}`,
        method: 'GET'
      }),
      providesTags: [API_TAG_TYPES.PAY_RUN_MEMBER_DETAILS],
      extraOptions: {
        failure: 'Failed to fetch member details',
        showToast: true
      }
    }),

    getSuggestedAdjustmentsForMember: builder.query<
      SuggestedAdjustmentsResponse,
      { teamId: number; merchantId: string; payRunId: string }
    >({
      query: ({ teamId, merchantId, payRunId }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/merchants/${merchantId}/pay-run/${payRunId}/suggested-adjustments`,
        method: 'GET'
      }),
      providesTags: [API_TAG_TYPES.PAY_RUN_MEMBER_DETAILS],
      extraOptions: {
        failure: 'Failed to fetch suggested adjustments',
        showToast: true
      }
    }),

    uploadAdjustmentReceipt: builder.mutation<UploadAdjustmentReceiptResponse, { teamId: number; image: File }>({
      query: ({ teamId, image }) => {
        const formData = new FormData();
        formData.append('image', image);

        return {
          url: `${API_VERSION.V1}/merchants/teams/${teamId}/pay-run/adjustments/upload-receipt`,
          method: 'POST',
          body: formData
        };
      },
      extraOptions: {
        failure: 'Failed to upload receipt',
        showToast: false
      }
    }),

    createAdjustment: builder.mutation<UpdateCreateResponse, CreateAdjustmentPayload>({
      query: payload => ({
        url: `${API_VERSION.V1}/merchants/teams/${payload.teamId}/pay-run/adjustments`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: [
        API_TAG_TYPES.PAY_RUN_MEMBER_DETAILS,
        API_TAG_TYPES.GET_PROFITABILITY_SUMMARY,
        API_TAG_TYPES.PAY_RUN_MEMBER_JOBS
      ],
      extraOptions: {
        failure: 'Failed to create adjustment',
        success: 'Adjustment created successfully',
        showToast: true
      }
    }),

    updateAdjustment: builder.mutation<UpdateCreateResponse, UpdateAdjustmentPayload>({
      query: ({ teamId, adjustmentId, ...payload }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/pay-run/adjustments/${adjustmentId}`,
        method: 'PUT',
        body: payload
      }),
      invalidatesTags: [API_TAG_TYPES.PAY_RUN_MEMBER_DETAILS],
      extraOptions: {
        failure: 'Failed to update adjustment',
        success: 'Adjustment updated successfully',
        showToast: true
      }
    }),

    updateAdjustmentStatus: builder.mutation<UpdateCreateResponse, UpdateAdjustmentStatusPayload>({
      query: ({ teamId, adjustmentId, status }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/adjustments/${adjustmentId}/status`,
        method: 'PUT',
        body: { status }
      }),
      invalidatesTags: [API_TAG_TYPES.PAY_RUN_MEMBER_DETAILS, API_TAG_TYPES.PAY_RUN_MEMBER_JOBS],
      extraOptions: {
        failure: 'Failed to update adjustment status',
        success: 'Adjustment status updated successfully',
        showToast: true
      }
    }),
    approveMemberPayRun: builder.mutation<
      ApproveMemberPayRunResponse,
      { teamId: number; payRunId: string; payload: ApproveMemberPayRunPayload }
    >({
      query: ({ teamId, payRunId, payload }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/pay-run/${payRunId}/merchant/approve`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['PayRunMemberDetails'],
      extraOptions: {
        failure: 'Failed to approve member pay run',
        success: 'Member pay run approved successfully',
        showToast: true
      }
    }),
    moveJobToPayRun: builder.mutation<
      void,
      { teamId: number; sourcePayRunId: string; targetPayRunId: string; jobId: string }
    >({
      query: ({ teamId, sourcePayRunId, targetPayRunId, jobId }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/pay-run/merchants/jobs/${jobId}/move`,
        method: 'POST',
        body: { sourcePayRunId, targetPayRunId }
      }),
      extraOptions: {
        failure: 'Failed to move job to pay run',
        success: 'Job moved to pay run successfully',
        showToast: true
      },
      invalidatesTags: [API_TAG_TYPES.PAY_RUN_MEMBER_DETAILS]
    }),
    moveBulkJobsToPayRun: builder.mutation<
      void,
      { teamId: number; sourcePayRunId: string; targetPayRunId: string; jobIds: string[] }
    >({
      query: ({ teamId, sourcePayRunId, targetPayRunId, jobIds }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/pay-run/merchants/jobs/move`,
        method: 'POST',
        body: { sourcePayRunId, targetPayRunId, jobIds }
      }),
      extraOptions: {
        failure: 'Failed to move jobs to pay run',
        success: 'Jobs moved to pay run successfully',
        showToast: true
      },
      invalidatesTags: [API_TAG_TYPES.PAY_RUN_MEMBER_DETAILS, API_TAG_TYPES.PAY_RUN_MEMBER_JOBS]
    }),
    getJobAdjustments: builder.query<GetJobAdjustmentsResponse, GetJobAdjustmentsRequest>({
      query: ({ teamId, jobId, ...params }) => {
        const queryParams = getUrlSearchParams({
          ...params
        });
        return {
          url: `${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/adjustments?${queryParams}`,
          method: 'GET'
        };
      }
    }),
    getSuggestedAdjustmentsForJob: builder.query<
      GetSuggestedAdjustmentsForJobResponse,
      { teamId: number; jobId: string }
    >({
      query: ({ teamId, jobId }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/jobs/${jobId}/suggested-adjustments`,
        method: 'GET'
      }),
      providesTags: [API_TAG_TYPES.SUGGESTED_ADJUSTMENTS_FOR_JOB]
    }),
    deleteAdjustment: builder.mutation<
      void,
      { teamId: number; jobId: string; adjustmentId: string; merchantId: string }
    >({
      query: ({ teamId, jobId, adjustmentId, merchantId }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/merchants/${merchantId}/jobs/${jobId}/adjustments/${adjustmentId}`,
        method: 'DELETE'
      }),
      invalidatesTags: [API_TAG_TYPES.GET_PROFITABILITY_SUMMARY, API_TAG_TYPES.PAY_RUN_MEMBER_JOBS],
      extraOptions: {
        failure: 'Failed to delete adjustment',
        success: 'Adjustment deleted successfully',
        showToast: true
      }
    }),
    updateJobAdjustment: builder.mutation<
      void,
      { teamId: number; jobId: string; adjustmentId: string; payload: UpdateJobAdjustmentPayload }
    >({
      query: ({ teamId, jobId, adjustmentId, payload }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/adjustments/${adjustmentId}`,
        method: 'PUT',
        body: payload
      }),
      invalidatesTags: [API_TAG_TYPES.GET_PROFITABILITY_SUMMARY, API_TAG_TYPES.PAY_RUN_MEMBER_JOBS],
      extraOptions: {
        failure: 'Failed to update adjustment',
        success: 'Adjustment updated successfully',
        showToast: true
      }
    }),
    deleteMerchantAdjustment: builder.mutation<
      DeleteMerchantAdjustmentResponse,
      { teamId: number; merchantId: string; adjustmentId: string }
    >({
      query: ({ teamId, merchantId, adjustmentId }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/pay-run/merchants/${merchantId}/adjustments/${adjustmentId}`,
        method: 'DELETE'
      }),
      extraOptions: {
        failure: 'Failed to delete adjustment',
        success: 'Adjustment deleted successfully',
        showToast: true
      },
      invalidatesTags: [API_TAG_TYPES.PAY_RUN_MEMBER_DETAILS, API_TAG_TYPES.PAY_RUN_MEMBER_JOBS]
    }),
    sendPayRunReport: builder.mutation<void, MerchantPayrunEmailRequest>({
      query: payload => ({
        url: `${API_VERSION.V1}/merchants/teams/${payload.teamId}/pay-run/${payload.payRunId}/send-report`,
        method: 'POST',
        body: payload
      }),
      extraOptions: {
        failure: 'Failed to send report',
        success: 'Report sent successfully',
        showToast: true
      }
    }),
    saveCustomColumns: builder.mutation<void, { teamId: number; payload: CustomizeColumnsPayload }>({
      query: ({ teamId, payload }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/custom-columns/save`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: [
        API_TAG_TYPES.PAY_PERIOD,
        API_TAG_TYPES.PAY_RUN_MEMBER_JOBS,
        API_TAG_TYPES.CUSTOMERS,
        API_TAG_TYPES.INVOICES_LIST,
        API_TAG_TYPES.DISPUTES_LIST
      ],
      extraOptions: {
        failure: 'Failed to save',
        success: 'Saved successfully',
        showToast: true
      }
    })
  })
});

export const {
  useSetPayRunSettingsMutation,
  useLazyGetCurrentPayPeriodQuery,
  useLazyGetPayRunSettingsQuery,
  useLazyGetRecentPayRunsQuery,
  useLazyGetPayRunsQuery,
  useGetPayRunMemberDetailsQuery,
  useGetSuggestedAdjustmentsForMemberQuery,
  useLazyGetSuggestedAdjustmentsForMemberQuery,
  useUploadAdjustmentReceiptMutation,
  useCreateAdjustmentMutation,
  useUpdateAdjustmentMutation,
  useUpdateAdjustmentStatusMutation,
  useApproveMemberPayRunMutation,
  useMoveJobToPayRunMutation,
  useMoveBulkJobsToPayRunMutation,
  useLazyGetPayRunsExportQuery,
  useGetJobAdjustmentsQuery,
  useGetSuggestedAdjustmentsForJobQuery,
  useDeleteAdjustmentMutation,
  useUpdateJobAdjustmentMutation,
  useDeleteMerchantAdjustmentMutation,
  useLazyGetJobAdjustmentsQuery,
  useSendPayRunReportMutation,
  useSaveCustomColumnsMutation
} = PayRunsBaseApi;
